import { useEffect, useRef, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import { OfferAPI } from '../../api/payments/offer/api';
import { getInitialParams } from './utils';

export function useOffersBootstrap() {
  const history = useHistory();
  const location = useLocation();
  const initialParams = useRef(getInitialParams(location.search)).current;
  const isFirstRender = useRef(true);

  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [totalRows, setTotalRows] = useState(0);
  const [perPage, setPerPage] = useState(initialParams.perPage);
  const [filterText, setFilterText] = useState(initialParams.search);
  const [resetPaginationToggle, setResetPaginationToggle] = useState(false);

  const updateQueryParams = (params) => {
    const searchParams = new URLSearchParams(location.search);
    Object.entries(params).forEach(([key, value]) => {
      if (value) {
        searchParams.set(key, value);
      } else {
        searchParams.delete(key);
      }
    });
    history.replace({ search: searchParams.toString() });
  };

  const fetchOffers = async (
    pageToFetch,
    rowsPerPage = perPage,
    activeOnly,
    search = filterText,
  ) => {
    setLoading(true);
    try {
      const response = await OfferAPI.list({
        page: pageToFetch - 1,
        pageSize: rowsPerPage,
        activeOnly,
        search: search,
      });
      const { content, totalElements } = response;
      setData(content || []);
      setTotalRows(totalElements || 0);
    } catch (error) {
      toast.warn('Erro ao carregar ofertas!');
      console.error(error)
    } finally {
      setLoading(false);
    }
  };

  const handlePageChange = (newPage) => {
    updateQueryParams({ page: newPage });
    fetchOffers(newPage);
  };

  const handlePerRowsChange = async (newPerPage, newPage) => {
    setPerPage(newPerPage);
    updateQueryParams({ perPage: newPerPage, page: newPage });
    await fetchOffers(newPage, newPerPage);
  };

  const handleAddOfferButton = () => {
    history.push('/payments/offers/add');
  };

  const handleFilterChange = (event) => setFilterText(event.target.value);

  const handleClearFilter = () => {
    if (filterText) {
      setResetPaginationToggle(!resetPaginationToggle);
      setFilterText('');
    }
  };

  useEffect(() => {
    fetchOffers(initialParams.page, initialParams.perPage, initialParams.search);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    const timeoutId = setTimeout(() => {
      updateQueryParams({ page: 1, search: filterText });
      fetchOffers(1, perPage, filterText);
    }, 400);
    return () => clearTimeout(timeoutId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterText]);

  return {
    data,
    loading,
    totalRows,
    perPage,
    filterText,
    resetPaginationToggle,
    initialParams,
    handlePageChange,
    handlePerRowsChange,
    handleAddOfferButton,
    handleFilterChange,
    handleClearFilter,
  };
}
